import { ColumnDef, PaginationState } from '@tanstack/react-table';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomStopLossFilter, CustomStopLoss } from 'common/models';
import { decimalToPercent } from 'common/utils';
import {
  Table,
  enumCell,
  valueCell,
  numberCell,
  dateCell,
  CircularIndicator,
} from 'core/components/uikit';

import CancelCustomStopLossButton from './CancelCustomStopLossButton';
import { useFetchCustomStopLossWithTimer } from './useFetchCustomStopLoss';
import DisclosureWithTitle from '../DisclosureWithTitle';

export type CustomStopLossTableProps = {
  filters?: CustomStopLossFilter;
  title?: string;
};

export const CustomStopLossTable = memo(
  ({ filters = {}, title }: CustomStopLossTableProps) => {
    const { t } = useTranslation();
    const { data, isLoading, fetch, startTimer, stopTimer, args } =
      useFetchCustomStopLossWithTimer(filters);

    const refetch = useCallback(async () => {
      if (args) await fetch(args);
    }, [args]);

    const columns = useMemo<ColumnDef<CustomStopLoss>[]>(
      () => [
        {
          header: t('components.customStopLossTable.headers.status'),
          accessorKey: 'status',
          cell: enumCell(),
        },
        {
          id: 'cancel-task',
          header: t('components.customStopLossTable.headers.cancel'),
          cell: ({ row }) => {
            const { id, status } = row.original;
            const showCancelButton = status === 'monitoring';

            if (showCancelButton) {
              return (
                <CancelCustomStopLossButton
                  id={id ?? ''}
                  refetchTable={refetch}
                />
              );
            }

            return '-';
          },
        },
        {
          header: t('components.customStopLossTable.headers.stopPrice'),
          accessorKey: 'stopPrice',
          cell: numberCell({ prefix: '$' }),
        },
        {
          header: t(
            'components.customStopLossTable.headers.sizeOfClosingPositionPart'
          ),
          accessorKey: 'sizeOfClosingPositionPart',
          cell: numberCell({
            suffix: '%',
            handler: (value) => decimalToPercent(value, 2),
          }),
        },
        {
          header: t('components.customStopLossTable.headers.orderType'),
          accessorKey: 'orderType',
          cell: enumCell(),
        },
        {
          header: t('components.customStopLossTable.headers.orderPriceType'),
          accessorKey: 'orderPriceType',
          cell: enumCell(),
        },
        {
          header: t(
            'components.customStopLossTable.headers.orderRefreshInterval'
          ),
          accessorKey: 'orderRefreshInterval',
          cell: valueCell(),
        },
        {
          header: t('components.customStopLossTable.headers.triggeredDate'),
          accessorKey: 'activatedAt',
          cell: (cellProps) => (
            <div className="whitespace-normal">{dateCell()(cellProps)}</div>
          ),
          minSize: 120,
        },
        {
          header: t('components.customStopLossTable.headers.replacedDate'),
          accessorKey: 'replacedAt',
          cell: (cellProps) => (
            <div className="whitespace-normal">{dateCell()(cellProps)}</div>
          ),
          minSize: 120,
        },
        {
          header: t('components.customStopLossTable.headers.token'),
          accessorKey: 'currentOrderToken',
          cell: valueCell(),
        },
      ],
      [t, refetch]
    );

    const toogleTimer = useCallback(
      (open: boolean) => {
        if (open) {
          stopTimer();
        } else {
          startTimer(5000);
        }
      },
      [stopTimer, startTimer]
    );

    const onPaginationChanged = useCallback(
      async ({ pageIndex, pageSize }: PaginationState) => {
        const newArgs = {
          ...(args ?? {}),
          page: pageIndex + 1,
          perPage: pageSize,
        };
        await fetch(newArgs);
      },
      [args]
    );

    return (
      <DisclosureWithTitle
        defaultOpen={false}
        title={
          <div className="flex flex-row items-center gap-2">
            <h4>{title ?? t('components.customStopLossTable.title')}</h4>
            {isLoading && <CircularIndicator size={24} />}
          </div>
        }
        onClick={toogleTimer}
      >
        <Table
          data={data?.items ?? []}
          columns={columns}
          totalCount={data?.filtered ?? 0}
          onPaginationChanged={onPaginationChanged}
          options={{
            initialState: {
              pagination: {
                pageIndex: (args?.page ?? 1) - 1,
                pageSize: args?.perPage,
              },
            },
          }}
        />
      </DisclosureWithTitle>
    );
  }
);

CustomStopLossTable.displayName = 'CustomStopLossTable';

export default CustomStopLossTable;
